import React, { useState } from "react";
import { KeyRound, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

export default function ChangePasswordModal({ open, onClose }) {
  const { theme } = useTheme();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
    setError(""); setDone(false); setLoading(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next === current) { setError("New password must differ from current."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setDone(true);
      setTimeout(() => { onClose(); reset(); }, 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); reset(); } }}>
      <DialogPortal>
        <DialogOverlay className="bg-black/80 backdrop-blur-md" />
        <DialogContent
          className="max-w-md w-[92vw] p-0 bg-[#0A0A0A] border text-white"
          style={{ borderColor: theme.color, boxShadow: `0 0 60px ${theme.color}55` }}
          data-testid="change-password-modal"
        >
          <DialogTitle className="sr-only">Change password</DialogTitle>
          <DialogDescription className="sr-only">
            Enter your current password and a new password.
          </DialogDescription>
          <div className="h-1 w-full" style={{ backgroundColor: theme.color }} />
          <div className="p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                  <KeyRound className="h-3 w-3 theme-text" /> Security
                </div>
                <h3 className="font-display font-black text-xl">Change password</h3>
              </div>
              <button
                onClick={() => { onClose(); reset(); }}
                className="text-zinc-400 hover:text-white transition-colors"
                data-testid="change-password-close"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {done ? (
              <div className="text-center py-6 space-y-4" data-testid="change-password-success">
                <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center pulse-glow" style={{ backgroundColor: theme.color }}>
                  <Check className="h-7 w-7 text-black" strokeWidth={3} />
                </div>
                <p className="text-sm text-zinc-300">Password updated.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <Field label="Current password" type="password" value={current} onChange={setCurrent} testid="cp-current" />
                <Field label="New password" type="password" value={next} onChange={setNext} testid="cp-next" />
                <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} testid="cp-confirm" />

                {error && <p className="text-sm text-red-400" data-testid="cp-error">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 font-display font-bold tracking-wide disabled:opacity-60"
                  style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }}
                  data-testid="cp-submit"
                >
                  {loading ? "UPDATING…" : "UPDATE PASSWORD"}
                </button>
              </form>
            )}
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm"
      />
    </label>
  );
}
